import startCase from 'lodash/startCase';

import type { DateGranularity } from '../../../filters/contexts/FilterProvider';
import {
  type Filter,
  FilterType,
  type PrimaryFilterId,
} from '../../../filters/types/filters';
import { getPrimaryFilterLabel } from '../../../filters/utils/common';
import { getFormattedDateString } from '../../../filters/utils/dateUtils';

type GetFileNameProps = {
  /** Leads the name in place of the primary filter's label, for a page without one. */
  context?: string;
  pageName: string;
  primaryFilterId?: PrimaryFilterId;
  chartTitle?: string;
  primarySelections?: { name: string }[];
  /** The dates the data covers, from useDataDates. */
  dataDates?: string;
};

const SEPARATOR = ' - ';
/** Whole-name cap: easy to scan, and leaves room for the folder path. */
const MAX_FILE_NAME_CHARS = 100;
const MAX_LISTED_ENTITIES = 3;
/** A shortened entity name below this length is dropped, not shown. */
const MIN_ENTITY_CHARS = 12;
const ILLEGAL_FILE_NAME_CHARS = /[\\/:*?"<>|]/g;

/** At most maxChars, cut at the last space, without a trailing ", " or " -". */
const cutAtWordBoundary = (text: string, maxChars: number): string => {
  if (text.length <= maxChars) return text;
  const cut = text.slice(0, maxChars + 1);
  const lastSpace = cut.lastIndexOf(' ');
  return (
    lastSpace > 0 ? cut.slice(0, lastSpace) : text.slice(0, maxChars)
  ).replace(/[\s,-]+$/, '');
};

/** "A, B, C", or "A, B +3 more": as many names as fit in maxChars. */
const formatEntities = (names: string[], maxChars: number): string => {
  const [first] = names;
  if (!first) return '';
  for (
    let shown = Math.min(names.length, MAX_LISTED_ENTITIES);
    shown > 0;
    shown--
  ) {
    const rest = names.length - shown;
    const label =
      names.slice(0, shown).join(', ') + (rest > 0 ? ` +${rest} more` : '');
    if (label.length <= maxChars) return label;
  }
  // Even one name is too long: shorten the name, then add the count, so the
  // count is never cut off.
  const count = names.length > 1 ? ` +${names.length - 1} more` : '';
  const room = maxChars - count.length;
  return room >= MIN_ENTITY_CHARS
    ? `${cutAtWordBoundary(first, room)}${count}`
    : '';
};

/** "<primary filter> - <page>": the PPT deck and slide title. */
export const getExportTitle = ({
  pageName,
  primaryFilterId,
  context,
}: Pick<
  GetFileNameProps,
  'pageName' | 'primaryFilterId' | 'context'
>): string =>
  [
    context ??
      (primaryFilterId ? getPrimaryFilterLabel(primaryFilterId) : undefined),
    startCase(pageName).trim(),
  ]
    .filter(Boolean)
    .join(SEPARATOR);

/**
 * "<primary filter> - <page> - <chart title> - <entities> - <data dates>",
 * within MAX_FILE_NAME_CHARS. The dates always stay; the entities get the
 * room left after the title parts, and are left out when too little remains.
 */
export const getFileName = ({
  context,
  pageName,
  primaryFilterId,
  chartTitle,
  primarySelections,
  dataDates,
}: GetFileNameProps): string => {
  const dates = dataDates ? `${SEPARATOR}${dataDates}` : '';
  const room = MAX_FILE_NAME_CHARS - dates.length;
  const head = cutAtWordBoundary(
    [getExportTitle({ pageName, primaryFilterId, context }), chartTitle?.trim()]
      .filter(Boolean)
      .join(SEPARATOR),
    room
  );
  const names = (primarySelections ?? [])
    .map((selection) => selection.name?.trim())
    .filter((name): name is string => Boolean(name));
  const entities = formatEntities(names, room - head.length - SEPARATOR.length);

  const name = [head, entities].filter(Boolean).join(SEPARATOR) + dates;
  // Page names, chart titles and entity names are user data.
  return name.replace(ILLEGAL_FILE_NAME_CHARS, '-');
};

/** A date as YYYY-MM-DD, or YYYY-MM at month granularity. */
export const toFileDate = (date: Date, granularity: DateGranularity = 'week') =>
  getFormattedDateString(date, granularity);

/** "start to end", or the one date when the range starts and ends on it. */
export const formatDateRange = (
  min: Date,
  max: Date,
  granularity: DateGranularity = 'week'
): string => {
  const start = toFileDate(min, granularity);
  const end = toFileDate(max, granularity);
  return start === end ? start : `${start} to ${end}`;
};

/**
 * The dates a resolved date filter covers, for a file name; '' if unset. Pass
 * the view granularity to show them as the date filter's chip does.
 */
export const formatDataDates = (
  filter: Filter | undefined,
  granularity: DateGranularity = 'week'
): string => {
  if (filter?.type === FilterType.DATE_RANGE && filter.min && filter.max) {
    return formatDateRange(filter.min, filter.max, granularity);
  }
  if (filter?.type === FilterType.DATE && filter.value) {
    return toFileDate(filter.value, granularity);
  }
  return '';
};
